// ── Following the verse being read ──────────────────────────────────────────
// The pure half of useFollowVerse: which verse a line belongs to, when the
// playing verse has drifted far enough to scroll, where to scroll to, and
// which input events mean the reader is scrolling by hand. Nothing here
// touches the DOM, so all of it can be tested without a browser.

// "Col|3|12": the same verse in any language, so a parallel page lights the
// verse in both columns.
export function verseKeyOf(v) {
  return v && v.book && v.verse != null ? `${v.book}|${Number(v.chapter)}|${Number(v.verse)}` : null;
}

// The data attributes the pages put on each verse block. Every value is
// quoted, so books such as "1Co" are safe in the selector.
export function verseSelector(v) {
  if (!verseKeyOf(v)) return null;
  const q = s => `"${String(s).replace(/["\\]/g, c => '\\' + c)}"`;
  return `[data-book=${q(v.book)}][data-chapter=${q(Number(v.chapter))}][data-verse=${q(Number(v.verse))}]`;
}

// On the Reading page a poetic verse runs over several lines, and only the
// first carries its reference. Each line's owning verse: its own reference,
// or the verse above it for the lines that continue it. A line just before a
// new chapter's first verse is a Psalm heading belonging to what follows, so
// it is left unowned rather than lit up with the end of the previous psalm.
// `parsed` is parseReadingLines' output for the same lines.
export function lineOwners(lines, parsed) {
  const owners = new Array(lines.length).fill(null);
  let nextStartsChapter = false;
  const leadsIntoChapter = new Array(lines.length).fill(false);
  for (let i = lines.length - 1; i >= 0; i--) {
    if (parsed[i]) nextStartsChapter = parsed[i].verse === 1;
    else leadsIntoChapter[i] = nextStartsChapter;
  }
  let current = null;
  for (let i = 0; i < lines.length; i++) {
    if (parsed[i]) { current = parsed[i]; owners[i] = current; continue; }
    if (!(lines[i] || '').trim()) continue;
    if (leadsIntoChapter[i]) { current = null; continue; }
    owners[i] = current;
  }
  return owners;
}

// How far down the screen the sticky bars reach, from the rects of the
// navbar, banner and toolbars. A bar counts only while it sits in the top
// part of the screen; an open panel that fills most of it (Today's Reading
// opened while pinned) is not a header and would leave no room to follow in.
export function headerInset(rects, viewportHeight) {
  let inset = 0;
  for (const r of rects) {
    if (!r || !r.height || r.bottom <= 0) continue;
    if (r.top >= viewportHeight * 0.4 || r.bottom >= viewportHeight * 0.6) continue;
    inset = Math.max(inset, r.bottom);
  }
  return inset;
}

// The middle third of the band the reader can actually see.
export function middleThird(band) {
  const h = band.bottom - band.top;
  return { top: band.top + h / 3, bottom: band.top + (2 * h) / 3 };
}

// Whether the verse at `rect` has left the middle third of `band` (both in
// viewport pixels). A short verse is judged by its centre. A verse taller than
// the middle third can never fit inside it, so it counts as in place while
// it still covers part of the middle third. Scrolling only when this says so
// keeps the page still for most verse changes instead of nudging it each time.
export function needsFollow(rect, band) {
  if (!rect || band.bottom <= band.top) return false;
  const mid = middleThird(band);
  if (rect.bottom - rect.top >= mid.bottom - mid.top) {
    return !(rect.top <= mid.bottom && rect.bottom >= mid.top);
  }
  const centre = (rect.top + rect.bottom) / 2;
  return centre < mid.top || centre > mid.bottom;
}

// The window scroll position that puts the verse at the centre of `band`.
// A verse too tall to centre is brought in with its first line a little below
// the headers instead, so the reader sees where it begins.
export function followScrollTop(rect, band, scrollY) {
  const h = band.bottom - band.top;
  const height = rect.bottom - rect.top;
  const want = height > h * 0.8
    ? band.top + h * 0.08
    : band.top + (h - height) / 2;
  return Math.max(0, Math.round(scrollY + rect.top - want));
}

// Keys that scroll the page when nothing focused swallows them.
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar']);

// Whether an input event is the reader scrolling the page by hand. Our own
// window.scrollTo never produces wheel, touch, key or pointer events, so
// scrolls we started are never mistaken for the reader's; that is why this
// looks at input rather than at scroll events (which the browser also fires
// for its own scroll anchoring when content above changes height).
// `e` is a plain description of the event:
//   type        'wheel' | 'touchmove' | 'keydown' | 'pointerdown'
//   deltaX/Y    wheel distance;  ctrlKey: a ctrl+wheel is a zoom, not a scroll
//   dx/dy       touch movement since touchstart
//   key, altKey, metaKey for keydown
//   editable    the target takes keys itself (input, button, audio, …)
//   inner       the target is inside a panel that scrolls on its own
//   onScrollbar a pointerdown on the page's own scrollbar
export function isUserScroll(e) {
  if (!e) return false;
  switch (e.type) {
    case 'wheel':
      return !e.inner && !e.ctrlKey && Math.abs(e.deltaY || 0) > 0;
    case 'touchmove': {
      // A sideways swipe turns the chapter on The Bible; small jitters are taps.
      const dx = Math.abs(e.dx || 0), dy = Math.abs(e.dy || 0);
      return !e.inner && dy > 10 && dy > dx;
    }
    case 'keydown':
      return SCROLL_KEYS.has(e.key) && !e.editable && !e.altKey && !e.metaKey && !e.ctrlKey;
    case 'pointerdown':
    case 'mousedown':
      return !!e.onScrollbar;
    default:
      return false;
  }
}
